import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import InfiniteScroll from 'react-infinite-scroll-component';
import tinycolor from "tinycolor2";

// API/Components
import axiosInstance from '../components/axios';
import obvmConfig from '../config';
import { dictToQueryString, formatDateTime } from '../components/enums';
import { AlarmEventToStatusText, EventTypeToColor } from '../components/alarm_enums';
import DateRangeFilterControl from '../components/filters/date_range_filter_control';
import EventTypeFilterDialog from '../components/filters/event_type_filter_dialog';
import SystemNameFilterDialog from '../components/filters/system_name_filter_dialog';
import { applyHierarchyFilters } from '../components/query_params_helpers';
import useHierarchy from '../components/hooks/useHierarchy';

// Material UI
import {
	Box,
	IconButton,
	Paper,
	Toolbar,
	Typography,
	Table,
	TableContainer,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Tooltip,
} from '@mui/material';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import FilterListIcon from '@mui/icons-material/FilterList';
// import RefreshIcon from '@mui/icons-material/Refresh';	

export default function Events() {	
	const [events, setEvents] = useState([]);
	const [moreData, setMoreData] = useState(true);
	const [apiPage, setApiPage] = useState(1); // Page number for the API call
	
	// Save filtering states and colorization
	const [dateRangeFiltering, setDateRangeFiltering] = useState({
		start: '',
		end: '',
	});
	const { hierarchy } = useHierarchy();
	const [nameFiltering, setNameFiltering] = useState({
		search: '',
		organization: '',
		region: '',
		facility: '',
		building: '',
	});
	const [eventTypeFiltering, setEventTypeFiltering] = useState({
		acknowledged: true,
		cleared: true,
		type: [],
	});
	const [isColorized, setIsColorized] = useState(true);

	// Dialog state management
	const [nameDialogOpen, setNameDialogOpen] = useState(false);
	const [typeDialogOpen, setTypeDialogOpen] = useState(false);

	const navigate = useNavigate();

	const eventsPerPage = obvmConfig.EVENTS.EVENTS_PER_PAGE;

	useEffect(() => {
		getEventData(true);
	}, [dateRangeFiltering, nameFiltering, eventTypeFiltering]);
	
	// Get event data from the API
	function getEventData(overwrite = true) {

		// Only attempt to get new data if there is more data to get
		if (moreData) {
			// Set the events api endpoint URL
			let url = `/events`;

			// Filter events based on name, hierarchy, and type options
			let queryParams = {};
			
			// Add the name/serial number filter to the query parameters
			if (nameFiltering && nameFiltering.search !== '') {
				queryParams['name'] = nameFiltering.search;
			}

			// Add the organization, region, facility, and building filters if they are set
			applyHierarchyFilters(queryParams, {
				org: nameFiltering.organization,
				reg: nameFiltering.region,
				fac: nameFiltering.facility,
				bldg: nameFiltering.building,
			});

			// Add the exclude acknowledged filter to the query parameters
			if (eventTypeFiltering && !eventTypeFiltering.acknowledged) {
				queryParams['ack'] = 'false';
			}

			// Add the exclude cleared filter to the query parameters
			if (eventTypeFiltering && !eventTypeFiltering.cleared) {
				queryParams['clear'] = 'false';
			}
			
			if (Array.isArray(eventTypeFiltering.type) && eventTypeFiltering.type.length > 0) {
				queryParams['type'] = eventTypeFiltering.type.join(',');
			}

			// Add the date range filter to the query parameters
			if (dateRangeFiltering.start) {
				queryParams['start'] = dateRangeFiltering.start;
			}

			if (dateRangeFiltering.end) {
				queryParams['end'] = dateRangeFiltering.end;
			}

			// Add the page number to the query parameters
			if (apiPage > 1) {
				queryParams['page'] = apiPage;
			}

			// Convert the query parameters to a query string
			url += dictToQueryString(queryParams);

			axiosInstance.get(url).then(response => {
				if (overwrite) {
					setEvents(response.data);
				} else {
					setEvents(prevEvents => [...prevEvents, ...response.data]);
				}

				if (response.data.length >= eventsPerPage) {
					setApiPage(apiPage + 1);
					setMoreData(true);
				} else {
					setMoreData(false);
				}
			});
		}
	}

	// Update the actual filtering options when the dialog is closed
	const handleNameDialogClose = (dialogFiltering) => {
		setNameDialogOpen(false);

		setApiPage(1);
		setMoreData(true);

		setNameFiltering(dialogFiltering);
	};
	
	// Update the page-level filter payload when the dialog is closed
	const handleTypeDialogClose = (dialogFiltering) => {
		setTypeDialogOpen(false);

		setApiPage(1);
		setMoreData(true);

		setEventTypeFiltering(dialogFiltering);
	};

	// Update the date range filtering when the date range filter control changes
	const handleDateRangeChange = useCallback((start_date, end_date) => {
		setApiPage(1);
		setMoreData(true);

		setDateRangeFiltering((prevDateRangeFiltering) => {
			if (prevDateRangeFiltering.start === start_date && prevDateRangeFiltering.end === end_date) {
				return prevDateRangeFiltering;
			}

			return {
				start: start_date,
				end: end_date,
			};
		});
	}, []);

	const SystemNameStyle = {
		minWidth: '200px',
		width: '200px',
	};

	const SerialStyle = {
		minWidth: '100px',
		width: '100px',
	};

	const DateTimeStyle = {
		minWidth: '190px',
		width: '190px',
	};

	const ClearedStyle = {
		minWidth: '190px',
		width: '190px',
		'@media (max-width:1280px)': {
			display: 'none',  // Hide the column on smaller windows
		}
	};

	const EventStyle = {
		minWidth: '400px',
		width: '50%',
	};

	const NoteStyle = {
		minWidth: '200px',
		width: '40%',
	};

	const AckStyle = {
		minWidth: '190px',
		width: '190px',
		'@media (max-width:1280px)': {
			display: 'none',  // Hide the column on smaller windows
		}
	};

	const AckUserStyle = {
		minWidth: '160px',
		width: '10%',
		'@media (max-width:1280px)': {
			display: 'none',  // Hide the column on smaller windows
		}
	};

	const RetrievedStyle = {
		minWidth: '190px',
		width: '190px',
		'@media (max-width:1280px)': {
			display: 'none',  // Hide the column on smaller windows
		}
	};

	return (
		<Paper
			padding='true'
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Toolbar disableGutters sx={{ paddingTop: 2, paddingX: 1.5, marginBottom: 1 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center" width='100%'>
					<Box
						align="left"
						fontFamily='Saira,sans-serif'
						fontWeight='600'
						sx={{ flexGrow: 1}}
					/>
					<Tooltip title="Toggle type colorization">
						<IconButton
							sx={{
								border: '1px solid',
								borderRadius: 1,
								color: isColorized ? 'primary.main' : 'grey.500',
							}}
							onClick={() => setIsColorized(!isColorized)}
						>
							<ColorLensIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title="Set location name and hierarchy filtering">
						<IconButton
							sx={{ border: '1px solid', borderRadius: 1, borderColor: nameDialogOpen ? 'primary.main' : 'grey.500', margin: 1 }}
							onClick={() => setNameDialogOpen(true)}
						>
							<FilterListIcon />
							<Typography
								fontFamily='Saira,sans-serif'
								fontWeight='600'
								sx={{ marginLeft: 1 }}
							>
								Name
							</Typography>
						</IconButton>
					</Tooltip>
					<Tooltip title="Set alarm type filtering">
						<IconButton
							sx={{
								border: '1px solid',
								borderRadius: 1,
								borderColor: typeDialogOpen ? 'primary.main' : 'grey.500',
								marginRight: 1
							}}
							onClick={() => setTypeDialogOpen(true)}
						>
							<FilterListIcon />
							<Typography
								fontFamily='Saira,sans-serif'
								fontWeight='600'
								sx={{ marginLeft: 1 }}
							>
								Type
							</Typography>
						</IconButton>
					</Tooltip>
					<DateRangeFilterControl onFilterChange={handleDateRangeChange} />
				</Box>
			</Toolbar>
			<SystemNameFilterDialog dialogOpen={nameDialogOpen} hierarchy={hierarchy} handleDialogClose={handleNameDialogClose} />
			<EventTypeFilterDialog dialogOpen={typeDialogOpen} handleDialogClose={handleTypeDialogClose} />
			<div
				id="scrollableDiv"
				style={{
					height: 'calc(100vh - 230px)',
					overflow: 'auto',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<InfiniteScroll
					dataLength={events.length}
					next={() => { getEventData(false) }}
					hasMore={moreData}
					scrollableTarget="scrollableDiv"
					style={{ overflow: 'visible' }}
					loader={
						<Typography fontFamily='Saira,sans-serif' fontWeight='600' sx={{ textAlign: 'center'}}>
							Loading events...
						</Typography>
					}
					endMessage={
						<Typography fontFamily='Saira,sans-serif' fontWeight='600' sx={{ textAlign: 'center'}}>
							No more events to load.
						</Typography>
					}
				>
					<TableContainer sx={{ margin: 0, padding: 0 }}>
						<Table size="small" sx={{ margin: 0, borderSpacing: 0 }}>
							<TableHead>
								<TableRow>
									<TableCell sx={{ ...SystemNameStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										System Name
									</TableCell>
									<TableCell sx={{ ...SerialStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Serial Num
									</TableCell>
									<TableCell sx={{ ...DateTimeStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Date/Time
									</TableCell>
									<TableCell sx={{ ...ClearedStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Date/Time Cleared
									</TableCell>
									<TableCell sx={{ ...EventStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Event
									</TableCell>
									<TableCell sx={{ ...NoteStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Note
									</TableCell>
									<TableCell sx={{ ...AckStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Acknowledged
									</TableCell>
									<TableCell sx={{ ...AckUserStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Ack. User
									</TableCell>
									<TableCell sx={{ ...RetrievedStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Retrieved
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{events.map((event, index) => (
									<TableRow
										key={index}
										onClick={() => navigate(`/location/${event.location_id}`)}
										sx={{
											cursor: 'pointer',
											backgroundColor: isColorized ? (
												tinycolor(EventTypeToColor(event.alarm_type, event.number)).lighten(15).toString()
											) : ( 
												index % 2 === 0 ? '#FFFFFF' : '#F2F2F2'
											),
											'&:hover': {
												backgroundColor: isColorized ? EventTypeToColor(event.alarm_type, event.number) : '#D9D9D9',
											},
										}}
									>
										<TableCell sx={SystemNameStyle} size="small" padding="checkbox">
											{event.location_name}
										</TableCell>
										<TableCell sx={SerialStyle} size="small" padding="checkbox">
											{event.serial_number}
										</TableCell>
										<TableCell sx={DateTimeStyle} size="small" padding="checkbox">
											{formatDateTime(event.datetime)}
										</TableCell>
										<TableCell sx={ClearedStyle} size="small" padding="checkbox">
											{formatDateTime(event.clear_datetime)}
										</TableCell>
										<TableCell sx={EventStyle} size="small" padding="checkbox">
											{AlarmEventToStatusText(event)}
										</TableCell>
										<TableCell sx={NoteStyle} size="small" padding="checkbox">
											{event.note_trimmed}
										</TableCell>
										<TableCell sx={AckStyle} size="small" padding="checkbox">
											{formatDateTime(event.ack_datetime)}
										</TableCell>
										<TableCell sx={AckUserStyle} size="small" padding="checkbox">
											{event.ack_user_trimmed}
										</TableCell>
										<TableCell sx={RetrievedStyle} size="small" padding="checkbox">
											{formatDateTime(event.retrieve_datetime)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</InfiniteScroll>
			</div>
		</Paper>
	);


}